import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Search, Download, CheckCircle2, Sparkles, AlertTriangle, Eye } from "lucide-react";

export function ProfileEditor({ profile, isAdminView, onChange }) {
  const p = profile.profile || {};
  const approved = profile.profile_approved || {};
  const [form, setForm] = useState({
    professional_title: p.professional_title || "", short_bio: p.short_bio || "", full_bio: p.full_bio || "",
    expertise: (p.expertise || []).join(", "), grade_levels: (p.grade_levels || []).join(", "),
    service_areas: (p.service_areas || []).join(", "),
    education_credentials: p.education_credentials || "", experience_presentations: p.experience_presentations || "",
  });
  const [suggestions, setSuggestions] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        expertise: form.expertise.split(",").map((s) => s.trim()).filter(Boolean),
        grade_levels: form.grade_levels.split(",").map((s) => s.trim()).filter(Boolean),
        service_areas: form.service_areas.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (isAdminView) await api.patch(`/admin/profiles/${profile.id}`, payload);
      else await api.patch("/profiles/me", payload);
      toast.success("Profile saved." + (!isAdminView ? " Bio changes are flagged for Admin review before proposal use." : ""));
      onChange();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const [resumeLink, setResumeLink] = useState("");

  const addResumeLink = async () => {
    const fd = new FormData();
    fd.append("external_url", resumeLink.trim());
    try {
      await api.post("/profiles/me/resume", fd);
      toast.success("Résumé link added as a new version; flagged for Admin review.");
      setResumeLink("");
      onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(isAdminView ? `/profiles/me/${kind}` : `/profiles/me/${kind}`, fd);
      toast.success(kind === "resume" ? "Résumé version uploaded. Prior versions are preserved; flagged for Admin review." : "Headshot uploaded; flagged for Admin review.");
      onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const extract = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/profiles/me/extract-suggestions");
      setSuggestions(data.suggestions);
      toast.success("Suggestions generated — review before applying. Nothing was saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const approve = async (kind, versionId) => {
    try {
      await api.patch(`/admin/profiles/${profile.id}`, { approve: kind, resume_version_id: versionId });
      toast.success("Approved snapshot frozen for proposal use.");
      onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const versions = p.resume_versions || [];
  const base = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-6" data-testid="profile-editor">
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-serif font-bold text-lg text-navy">Profile details</h2>
          {profile.bio_review_pending && <span className="badge-draft" data-testid="bio-review-flag"><AlertTriangle size={11} /> Bio changes await Admin review</span>}
        </div>
        {!isAdminView && (
          <button className="btn-outline !py-1.5 text-xs" onClick={extract} disabled={busy || !versions.length}
            data-testid="extract-suggestions-button">
            <Sparkles size={13} /> {versions.length ? "Extract profile suggestions from my résumé (AI — review required)" : "Upload a résumé first to use AI suggestions"}</button>
        )}
        {suggestions && (
          <div className="agent-panel p-4 space-y-2" data-testid="ai-suggestions-panel">
            <div className="text-xs font-black text-[#92400E]">AI suggestions — review each before applying. Never auto-saved, never invented.</div>
            {Object.entries(suggestions).map(([k, v]) => (
              <div key={k} className="flex items-start justify-between gap-3 text-xs">
                <div><b className="text-navy">{k.replace(/_/g, " ")}:</b> <span className="text-[#57534E]">{Array.isArray(v) ? v.join(", ") : v || "—"}</span></div>
                <button className="btn-ghost !py-0.5 !px-2 text-[11px] shrink-0" data-testid={`apply-suggestion-${k}`}
                  onClick={() => setForm({ ...form, [k]: Array.isArray(v) ? v.join(", ") : v })}>Apply</button>
              </div>
            ))}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="label">Professional title</label>
            <input className="input" value={form.professional_title} data-testid="profile-title-input"
              onChange={(e) => setForm({ ...form, professional_title: e.target.value })} /></div>
          <div><label className="label">Company email</label>
            <input className="input" value={profile.email} disabled data-testid="profile-email" /></div>
          <div className="sm:col-span-2"><label className="label">Short bio (1–2 sentences)</label>
            <textarea className="input" value={form.short_bio} data-testid="profile-short-bio"
              onChange={(e) => setForm({ ...form, short_bio: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className="label">Full bio</label>
            <textarea className="input min-h-[120px]" value={form.full_bio} data-testid="profile-full-bio"
              onChange={(e) => setForm({ ...form, full_bio: e.target.value })} /></div>
          <div><label className="label">Areas of expertise (comma-separated)</label>
            <input className="input" value={form.expertise} data-testid="profile-expertise"
              onChange={(e) => setForm({ ...form, expertise: e.target.value })} /></div>
          <div><label className="label">Grade levels served</label>
            <input className="input" value={form.grade_levels} placeholder="K-2, 3-5…" data-testid="profile-grades"
              onChange={(e) => setForm({ ...form, grade_levels: e.target.value })} /></div>
          <div><label className="label">Geographic service areas</label>
            <input className="input" value={form.service_areas} data-testid="profile-areas"
              onChange={(e) => setForm({ ...form, service_areas: e.target.value })} /></div>
          <div><label className="label">Education & credentials</label>
            <input className="input" value={form.education_credentials} data-testid="profile-education"
              onChange={(e) => setForm({ ...form, education_credentials: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className="label">Relevant experience & presentations</label>
            <textarea className="input" value={form.experience_presentations} data-testid="profile-experience"
              onChange={(e) => setForm({ ...form, experience_presentations: e.target.value })} /></div>
        </div>
        <p className="text-[11px] text-[#78716C]">No field is required. Personal details in your résumé (address, phone, references) are never automatically included in proposals — Admin reviews the exact material selected for external use.</p>
        <button className="btn-primary" onClick={save} disabled={busy} data-testid="profile-save-button">{busy ? "Saving…" : "Save profile"}</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card p-6 space-y-3" data-testid="headshot-card">
          <h3 className="font-serif font-bold text-navy">Headshot</h3>
          <div className="flex items-center gap-4">
            {p.headshot_path
              ? <img src={`${base}/api/profiles/${profile.id}/headshot`} alt="Headshot" className="w-20 h-20 rounded-full object-cover border border-[#E7E1D4]" data-testid="headshot-image" />
              : <div className="w-20 h-20 rounded-full bg-sand flex items-center justify-center text-[#A8A29E] text-xs" data-testid="headshot-empty">None</div>}
            {!isAdminView && (
              <label className="btn-outline !py-1.5 text-xs cursor-pointer" data-testid="headshot-upload">
                Upload headshot<input type="file" accept="image/*" className="hidden" onChange={(e) => upload("headshot", e.target.files?.[0])} /></label>)}
          </div>
          {approved.headshot_path
            ? <span className="badge-approved" data-testid="headshot-approved-badge">Approved for proposals</span>
            : p.headshot_path && <span className="badge-draft">Not approved for proposals</span>}
          {isAdminView && p.headshot_path && p.headshot_path !== approved.headshot_path && (
            <button className="btn-primary !py-1.5 text-xs" onClick={() => approve("headshot")} data-testid="approve-headshot-button">
              <CheckCircle2 size={13} /> Approve headshot for proposals</button>)}
        </div>

        <div className="card p-6 space-y-3" data-testid="resume-card">
          <h3 className="font-serif font-bold text-navy">Résumé versions</h3>
          {!isAdminView && (
            <div className="flex flex-wrap items-center gap-2">
              <label className="btn-outline !py-1.5 text-xs cursor-pointer" data-testid="resume-upload">
                Upload résumé (PDF or DOCX)
                <input type="file" accept=".pdf,.docx" className="hidden" onChange={(e) => upload("resume", e.target.files?.[0])} /></label>
              <span className="text-xs text-[#78716C]">or</span>
              <input className="input !py-1.5 text-xs flex-1 min-w-[220px]" placeholder="https://docs.google.com/… (Google Doc share link)"
                value={resumeLink} data-testid="resume-link-input" onChange={(e) => setResumeLink(e.target.value)} />
              <button className="btn-outline !py-1.5 text-xs" onClick={addResumeLink} disabled={!resumeLink.trim()}
                data-testid="resume-link-add">Add link</button>
            </div>)}
          <ul className="space-y-2">
            {versions.map((v) => (
              <li key={v.id} className="flex items-center justify-between gap-2 rounded-lg border border-[#E7E1D4] px-3 py-2" data-testid={`resume-version-${v.id}`}>
                <div className="text-xs min-w-0">
                  <b className="text-navy">{v.version_label}</b> · {v.filename || v.external_url}
                  <div className="text-[#A8A29E]">{new Date(v.uploaded_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {v.external_url ? (
                    <a href={v.external_url} target="_blank" rel="noreferrer"
                      className="btn-ghost !py-1 !px-2 text-xs" data-testid={`resume-view-${v.id}`}><Eye size={12} /></a>
                  ) : (<>
                    <a href={`${base}/api/profiles/${profile.id}/resume/${v.id}`} target="_blank" rel="noreferrer"
                      className="btn-ghost !py-1 !px-2 text-xs" data-testid={`resume-view-${v.id}`}><Eye size={12} /></a>
                    <a href={`${base}/api/profiles/${profile.id}/resume/${v.id}`} download
                      className="btn-ghost !py-1 !px-2 text-xs" data-testid={`resume-download-${v.id}`}><Download size={12} /></a>
                  </>)}
                  {v.approved_for_proposals
                    ? <span className="badge-approved" data-testid={`resume-approved-${v.id}`}>Approved</span>
                    : isAdminView && (
                      <button className="btn-ghost !py-1 !px-2 text-xs text-[#065F46]" data-testid={`resume-approve-${v.id}`}
                        onClick={() => approve("resume", v.id)}>Approve</button>)}
                </div>
              </li>
            ))}
            {versions.length === 0 && <li className="text-xs text-[#A8A29E]">No résumé uploaded yet.</li>}
          </ul>
          {isAdminView && (profile.bio_review_pending || !approved.short_bio) && (p.short_bio || p.full_bio) && (
            <button className="btn-primary !py-1.5 text-xs" onClick={() => approve("bio")} data-testid="approve-bio-button">
              <CheckCircle2 size={13} /> Approve current bios for proposals</button>)}
        </div>
      </div>
    </div>
  );
}

export default function MyProfile() {
  const [profile, setProfile] = useState(null);
  const load = () => api.get("/profiles/me").then((r) => setProfile(r.data));
  useEffect(() => { load(); }, []);
  if (!profile) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="my-profile-loading">Loading profile…</div>;
  return (
    <div className="space-y-6" data-testid="my-profile-page">
      <div className="fade-up">
        <p className="eyebrow">My Profile & Résumé</p>
        <h1 className="section-title text-3xl mt-1">{profile.name}</h1>
        <p className="mt-3 text-sm text-[#57534E] max-w-2xl">
          Maintain your qualifications and materials for staffing and proposals. You can view and update only
          your own profile. Admin approves specific bios, résumés, and headshots before they're used in proposals.
        </p>
      </div>
      <ProfileEditor profile={profile} isAdminView={false} onChange={load} />
    </div>
  );
}
